import { createContextualCan } from "@casl/react";
import { ReactNode, createContext, useContext } from "react";

import { useUser } from "@/modules/auth/hooks/use-user";

import { AppAbility, defineAbilitiesFor } from "./abilities";

// Create default empty ability
const defaultAbility = defineAbilitiesFor(undefined);

const AbilityContext = createContext<AppAbility>(defaultAbility);

const Can = createContextualCan(AbilityContext.Consumer);

const useAbility = () => {
  return useContext(AbilityContext);
};

interface AbilityProviderProps {
  children?: ReactNode;
}

const AbilityProvider = ({ children }: AbilityProviderProps) => {
  const { user } = useUser();

  const ability = defineAbilitiesFor(user);

  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
};

const useBusinessAbilities = () => {
  const ability = useAbility();

  return {
    canManageProcedures: ability.can("manage", "Procedure"),
    canManageEnergySavingCertificates: ability.can(
      "manage",
      "EnergySavingCertificate",
    ),
    canManageSignatures: ability.can("manage", "SignatureRequest"),
    canManageDeliveries: ability.can("manage", "DeliveryTracking"),
    canManageBilling: ability.can("manage", "Billable"),
    canManageCollegeFolders: ability.can("manage", "CollegeFolder"),
    canSeeDgtStatusViews: ability.can("manage", "DgtStatusView"),
  };
};

export {
  Can,
  useAbility,
  AbilityProvider,
  type AppAbility,
  defineAbilitiesFor,
  AbilityContext,
  useBusinessAbilities,
};
